import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {formAdapter, getYearMonthDayFormat} from "../../utils";
import { errorToast, successToast } from "../../../../../shared/toastify";
import { baseUrl } from "../../../../../constants/constants";
import {INews} from "../../../adminPage";
import {ITags} from "../../../../public/NewsPage/news";


export interface IUpdateNews {
    id: number;
    name: string;
    description: string;
    source_link: string;
    dzen_link: string;
    tg_link: string;
    image: FileList | null;
    create_date: string;
    tags_id: { [key: string]: boolean };
}

interface Props {
    close: () => void;
    tags: ITags[];
    news: INews;
}

const UpdateNewsForm = ({ tags, close, news}: Props) => {
    const [initialData, setInitialData] = useState<IUpdateNews | null>(null);

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors },
        setError,
    } = useForm<IUpdateNews>({
        defaultValues: {
            tags_id: {},
            create_date: getYearMonthDayFormat(new Date(news.create_date))
        },
    });

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const res = await fetch(`${baseUrl}news/${news.id}`);
                if (res.ok) {
                    const data = await res.json();

                    const tags_id = (data.tags as ITags[])
                        .filter((tag) => tag.id)
                        .map((tag) => tag.id)

                    const formattedTags = tags.reduce((acc, tag) => {
                        acc[tag.id] = tags_id.includes(tag.id);
                        return acc;
                    }, {} as { [key: string]: boolean });

                    setInitialData({
                        ...data,
                        tags_id: formattedTags,
                        create_date: new Date(data.create_date),
                    });
                } else {
                    errorToast("Ошибка при загрузке новости");
                }
            } catch (err) {
                console.error(err);
                errorToast("Ошибка при загрузке новости");
            }
        };

        fetchNews();
    }, [news.id, tags]);

    useEffect(() => {
        if (initialData) {
            setValue("name", initialData.name);
            setValue("description", initialData.description);
            setValue("source_link", initialData.source_link);
            setValue("dzen_link", initialData.dzen_link);
            setValue("tg_link", initialData.tg_link);
            setValue("create_date", getYearMonthDayFormat(new Date(initialData.create_date)));

            Object.keys(initialData.tags_id).forEach((tagId) => {
                setValue(`tags_id.${tagId}`, initialData.tags_id[tagId]);
            });
        }
    }, [initialData, setValue]);

    const submit: SubmitHandler<IUpdateNews> = async (data: any) => {
        try {
            const selectedTags = Object.values(data.tags_id).some((isSelected) => isSelected);
            if (!selectedTags) {
                setError("tags_id", { type: "manual", message: "Выберите хотя бы один тег" });
                // return (<p className={'wrong-message'}>Выберите хотя бы один тег</p>);
                return
            }

            const adaptedData = formAdapter(data, tags);

            const isoDate = new Date(data.create_date).toISOString();

            const formData = new FormData();

            formData.append("name", adaptedData.name);
            formData.append("description", adaptedData.description);
            formData.append("source_link", adaptedData.source_link);
            formData.append("dzen_link", adaptedData.dzen_link);
            formData.append("tg_link", adaptedData.tg_link);
            if (data.image && data.image[0]) {
                formData.append("image", data.image[0]);
            }
            formData.append("create_date", isoDate);
            adaptedData.tags_id.forEach((value) => formData.append('tags_id', value.toString()));

            const res = await fetch(`${baseUrl}news/${news.id}`, {
                method: "PUT",
                body: formData,
            });

            if (res.status === 200) {
                successToast("Новость успешно обновлена");
                close();
            } else if (res.status === 401) {
                setError("root", { type: "custom", message: "" });
                errorToast("Ошибка");
            }
        } catch (err) {
            console.log(err);
        }
    };

    if (!initialData) {
        return <div>Загрузка...</div>;
    }

    return (
        <form className="regForm" onSubmit={handleSubmit(submit)}>
            <input
                type="text"
                className="regInput"
                placeholder="Заголовок новости"
                {...register("name", { required: true })}
            />
            {errors.name && <p className="wrong-message">Это поле обязательно</p>}

            <textarea
                className="regInput"
                placeholder="Текст новости"
                {...register("description", { required: true })}
            />

            <input
                type="text"
                className="regInput"
                placeholder="Ссылка на вк"
                {...register("source_link", { required: true })}
            />
            {errors.source_link && <p className="wrong-message">Это поле обязательно</p>}

            <input
                type="text"
                className="regInput"
                placeholder="Ссылка на Дзен"
                {...register("dzen_link")}
            />

            <input
                type="text"
                className="regInput"
                placeholder="Ссылка на Телеграм"
                {...register("tg_link")}
            />

            <span className="regInput__file">
                <p>Изображение записи</p>
                <input
                    type="file"
                    {...register("image")}
                    accept="image/jpg, image/jpeg, image/webp, image/png"
                />
            </span>

            <label className="regLabel">
                Дата публикации
                <input
                    type="date"
                    className="regInput"
                    {...register("create_date", { required: true })}
                />
            </label>

            <fieldset className="account-label" style={{alignItems:'start'}}>
                Теги
                {tags.map((i) => (
                    <span key={i.id}>
                        <Controller
                            name={`tags_id.${i.id}`}
                            control={control}
                            defaultValue={false}
                            render={({ field }) => (
                                <input
                                    type="checkbox"
                                    className="regInput__checkbox-galka"
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                />
                            )}
                        />
                        <label className="account-label__radio__label">{i.name}</label>
                    </span>
                ))}
            </fieldset>

            <button className="button form-btn">Обновить</button>
        </form>
    );
};

export default UpdateNewsForm;